// External Imports
import { h } from 'preact';
import { Helmet } from 'react-helmet';
import { useEffect } from 'preact/hooks';
import MarketingTracker from '@lessonnine/marketing-tracker';
import {
  BabbelFonts,
  ResetStyles,
  ServerBabbelThemeProvider,
  Stack,
  styled,
} from '@lessonnine/design-system.lib';

// Internal Imports
import { AppContext } from '../../state/context';
import { ClassesSection } from '../ClassesSection/ClassesSection';
import { Footer } from '../Footer/Footer';
import { Header } from '../Header/Header';
import { HelpSection } from '../HelpSection/HelpSection';
import { HeroSection } from '../HeroSection/HeroSection';
import { ThreeValueProp } from '../ThreeValueProp/ThreeValueProp';
import { TestimonialsSection } from '../TestimonialsSection/TestimonialsSection';
import { getDefaultLearnLanguage, getSortedLearnLanguages } from '../../constants/learnLanguages';
import { TeachersSection } from '../TeachersSection/TeachersSection';
import { GettingStartedSection } from '../GettingStartedSection/GettingStartedSection';
import { SelectLanguageSection } from '../SelectLanguageSection/SelectLanguageSection';
// import { AnnouncementBanner } from '../AnnouncementBanner/AnnouncementBanner';

const AppWithBackground = styled(Stack)`
  background-color: ${({ theme }) => theme.color.neutral.surfaceDefault};
`;

// Component Definition
function App({ translations, locale }) {
  const translationsContext = { t: translations.live_index };
  const localeLearnLanguages = getSortedLearnLanguages(locale);
  const languageCode = getDefaultLearnLanguage(locale);
  const redirectUrl = `https://my.babbel.com/${locale}/prices/live`;
  const getDropdownLink = (learnLanguage = languageCode) => {
    const isEnglishLocale = locale === 'en';
    const productPreviewVariant = isEnglishLocale ? 'default_registration' : 'default_live';
    const welcomeSpaUrl = 'https://my.babbel.com/en/welcome';
    const registrationLink = `https://my.babbel.com/${locale}/product-preview/${learnLanguage}/${productPreviewVariant}/node/0`;
    return isEnglishLocale
      ? `${welcomeSpaUrl}?learn_lang=${learnLanguage}`
      : `${registrationLink}/?return_to=${redirectUrl}`;
  };
  const forBusinessesLink = `https://www.babbelforbusiness.com/${locale}`;
  const loginLink = `https://my.babbel.com/${locale}/authentication/login/email`;

  useEffect(() => {
    const { location } = window;
    const trackingDisabled = new URL(location.href).searchParams.has('disableTracking');
    if (location.hostname !== 'localhost' && !trackingDisabled) {
      const campaignsTracker = new MarketingTracker.CampaignsTracker({
        host: 'https://go.babbel.com/',
      });
      campaignsTracker.trackPageview(`${locale === 'en' ? 'usa' : locale}-live`);

      const gtmTracker = MarketingTracker.gtm;
      gtmTracker.load();
      gtmTracker.init({ locale });
    }
  }, [locale]);

  return (
    <AppContext.Provider
      // eslint-disable-next-line react/jsx-no-constructed-context-values -- TODO: fix this
      value={{
        ...translationsContext,
        forBusinessesLink,
        getDropdownLink,
        learnLanguages: localeLearnLanguages,
        locale,
        loginLink,
      }}
    >
      <Helmet htmlAttributes={{ lang: locale }}>
        <meta content={translationsContext.t.seo.description} name="description" />
        <meta content={locale} name="babbel:locale" />
        <link
          href={`https://${locale === 'en' ? 'www' : locale}.babbel.com/live`}
          rel="canonical"
        />
        <title>{translationsContext.t.seo.title}</title>
      </Helmet>
      <ServerBabbelThemeProvider defaultMode="light">
        <AppWithBackground>
          {/* <AnnouncementBanner /> */}
          <ResetStyles />
          <BabbelFonts />
          <Header />
          <HeroSection />
          <ThreeValueProp />
          <TestimonialsSection />
          <ClassesSection />
          <TeachersSection />
          <GettingStartedSection />
          <SelectLanguageSection />
          <HelpSection />
          <Footer />
        </AppWithBackground>
      </ServerBabbelThemeProvider>
    </AppContext.Provider>
  );
}

export { App };
